import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FileText, 
  CreditCard, 
  ClipboardList, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ArrowRight,
  User,
  Phone,
  Mail
} from 'lucide-react';
import Header from '../common/Header';
import TokenRecovery from '../auth/TokenRecovery';
import SessionExpired from '../auth/SessionExpired';
import { useApplicationFee } from '../../hooks/useApplicationFee';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [showTokenRecovery, setShowTokenRecovery] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  // Removed zohoDataRefreshTrigger state that was causing infinite loop
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [hasLoadedStatus, setHasLoadedStatus] = useState(false);
  const [lastUserId, setLastUserId] = useState(null);
  const [isStage1Loading, setIsStage1Loading] = useState(false);
  const { user, logout, isAuthenticated, loading: authLoading, updateUser } = useAuth();
  const navigate = useNavigate();

  // Same NEET-Leaders-batch detection/fee logic used on the payment page —
  // refreshes Zoho data itself so this doesn't show a stale login-time grade.
  const { applicationFeeDisplay } = useApplicationFee();

  useEffect(() => {
    console.log('🔍 Dashboard: Component mounted, checking token...');
    const token = localStorage.getItem('token');
    console.log('🔍 Dashboard: Token from localStorage:', token ? token.substring(0, 20) + '...' : 'undefined');
    console.log('🔍 Dashboard: AuthContext state:', { user, isAuthenticated, authLoading });
    
    // Don't redirect if we're still loading - let AuthContext handle authentication
    if (authLoading) {
      console.log('🔄 Dashboard: AuthContext still loading, waiting...');
      return;
    }
    
    // Check for token recovery scenario
    if (token && !isAuthenticated && !authLoading) {
      console.log('⚠️ Dashboard: Token exists but not authenticated - potential token issue');
      setShowTokenRecovery(true);
      return;
    }
    
    // Check for session expired scenario (no token, not loading)
    if (!token && !isAuthenticated && !authLoading) {
      console.log('⚠️ Dashboard: No token and not authenticated - session expired');
      setShowSessionExpired(true);
      return;
    }
    
    // Only redirect if we're definitely not authenticated and not loading (fallback)
    if (!isAuthenticated && !authLoading) {
      console.log('❌ Dashboard: Not authenticated and not loading, showing session expired');
      setShowSessionExpired(true);
      return;
    }
    
    // Try to load Zoho CRM data from localStorage if user doesn't have it
    if (!user?.zohoData) {
      console.log('🔄 Dashboard: User missing Zoho data, trying to load from localStorage...');
      const zohoData = localStorage.getItem('zohoCRMData');
      if (zohoData) {
        try {
          const parsed = JSON.parse(zohoData);
          console.log('✅ Dashboard: Loaded Zoho data from localStorage:', parsed);
          // Note: We can't directly update the user object here, but this helps with debugging
        } catch (error) {
          console.log('⚠️ Dashboard: Invalid Zoho data in localStorage:', error);
        }
      }
    }
    
    // Intelligent data refresh strategy - only clear if actually stale
    const zohoData = localStorage.getItem('zohoCRMData');
    if (zohoData) {
      try {
        const parsedData = JSON.parse(zohoData);
        // Check if data is stale (missing critical fields or has old timestamps)
        const isStale = !parsedData.leadId || 
                       !parsedData.applicationId || 
                       !parsedData.Application_Stage_1_Filled ||
                       (parsedData.lastUpdated && parsedData.lastUpdated < (Date.now() - 24 * 60 * 60 * 1000)); // 24 hours old
        
        if (isStale) {
          console.log('⚠️ Dashboard: Detected stale Zoho CRM data, clearing for refresh');
          localStorage.removeItem('zohoCRMData');
        } else {
          console.log('✅ Dashboard: Zoho CRM data is fresh, keeping it');
        }
      } catch (error) {
        console.log('⚠️ Dashboard: Invalid Zoho CRM data in localStorage, clearing');
        localStorage.removeItem('zohoCRMData');
      }
    } else {
      console.log('ℹ️ Dashboard: No Zoho CRM data found in localStorage');
    }
    
    // Reset loading state if user has changed
    if (user && user.id !== lastUserId) {
      console.log('🔄 Dashboard: User changed, resetting loading state');
      setHasLoadedStatus(false);
      setLastUserId(user.id);
    }
    
    // Only load application status if we haven't loaded it yet
    if (!hasLoadedStatus) {
      console.log('🔄 Dashboard: Loading application status for the first time...');
      setHasLoadedStatus(true);
      loadApplicationStatus();
    } else {
      console.log('ℹ️ Dashboard: Application status already loaded, skipping...');
    }
  }, [user, isAuthenticated, authLoading, navigate, hasLoadedStatus]);

  // Real-time payment status refresh when dashboard becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated && user) {
        console.log('🔄 Dashboard: Page became visible, refreshing payment status...');
        refreshPaymentStatus();
      }
    };

    const handleFocus = () => {
      if (isAuthenticated && user) {
        console.log('🔄 Dashboard: Window focused, refreshing payment status...');
        refreshPaymentStatus();
      }
    };

    // Listen for page visibility changes and window focus
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup listeners
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, user]);

  // Removed problematic useEffect that was causing infinite loop

  // Token recovery handlers
  const handleTokenRetry = () => {
    console.log('🔄 Dashboard: Retrying token validation...');
    setRetryCount(prev => prev + 1);
    setShowTokenRecovery(false);
    // Force a page reload to retry authentication
    window.location.reload();
  };

  const handleTokenLogin = () => {
    console.log('🚪 Dashboard: Redirecting to login...');
    setShowTokenRecovery(false);
    navigate('/login');
  };

  // Session expired handlers
  const handleSessionRetry = () => {
    console.log('🔄 Dashboard: Retrying session...');
    setRetryCount(prev => prev + 1);
    setShowSessionExpired(false);
    // Force a page reload to retry authentication
    window.location.reload();
  };

  const handleSessionLogin = () => {
    console.log('🚪 Dashboard: Redirecting to login from session expired...');
    setShowSessionExpired(false);
    navigate('/login');
  };

  const loadApplicationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Dashboard: loadApplicationStatus - Token check:', token ? token.substring(0, 20) + '...' : 'undefined');
      
      // Don't redirect if we're still loading - let the useEffect handle redirects
      if (!token && !authLoading) {
        console.log('❌ Dashboard: No token found and not loading, showing session expired');
        setShowSessionExpired(true);
        return;
      }
      
      // If we're still loading, don't make API calls yet
      if (authLoading) {
        console.log('🔄 Dashboard: AuthContext still loading, skipping API calls');
        return;
      }

      // Force refresh Zoho data on every dashboard load
      console.log('🔄 Dashboard: Force refreshing Zoho data on dashboard load...');
      setIsUpdatingStatus(true);
      try {
        const refreshResponse = await fetch('/api/auth/refresh-zoho-data', {
          method: 'POST',
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          }
        });
        
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          console.log('✅ Dashboard: Zoho data refreshed successfully:', refreshData);
          // Update token if new one provided
          if (refreshData.token) {
            localStorage.setItem('token', refreshData.token);
            console.log('✅ Dashboard: Token updated with fresh data');
            
            // Update the user context with the new token data
            // Decode the new token to get updated user data
            try {
              const tokenPayload = JSON.parse(atob(refreshData.token.split('.')[1]));
              if (tokenPayload.user && tokenPayload.user.zohoData) {
                console.log('🔄 Dashboard: Updating user context with fresh Zoho data from new token');
                console.log('🔄 Dashboard: New user data from token:', tokenPayload.user);
                
                // Update the user context with the new data
                updateUser(tokenPayload.user);
                console.log('✅ Dashboard: User context updated with fresh Zoho data');
              }
            } catch (tokenError) {
              console.log('⚠️ Dashboard: Error decoding new token:', tokenError);
            }
          }
          
          // If we got fresh Zoho data, store it
          if (refreshData.zohoData) {
            const dataWithTimestamp = {
              ...refreshData.zohoData,
              lastUpdated: Date.now()
            };
            localStorage.setItem('zohoCRMData', JSON.stringify(dataWithTimestamp));
            console.log('✅ Dashboard: Fresh Zoho CRM data stored with timestamp');
            
            // Status will be updated automatically when user.zohoData changes
            console.log('🔄 Dashboard: Zoho data updated, status will refresh automatically');
          }
        } else {
          console.log('⚠️ Dashboard: Zoho data refresh failed, continuing with existing data');
        }
      } catch (refreshError) {
        console.log('⚠️ Dashboard: Zoho data refresh error, continuing with existing data:', refreshError);
      }

      console.log('🔍 Dashboard: Making API call to /api/dashboard/data with token:', token.substring(0, 20) + '...');
      
      const response = await fetch('/api/dashboard/data', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('🔍 Dashboard: API response status:', response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log('🔍 Dashboard: Received dashboard API response:', responseData);
        
        // Extract data from the API response structure
        const data = responseData.data || responseData;
        console.log('🔍 Dashboard: Extracted data:', data);
        
        // Check if we have meaningful data
        if (data && (data.dashboardStages || data.stage1 || data.payment || data.stage2)) {
          setApplicationStatus(data);
          console.log('✅ Dashboard: Application status set with dashboard data');
        } else {
          console.log('⚠️ Dashboard: API returned empty data, using fallback');
          // Use fallback data when API returns empty results
          setApplicationStatus({
            stage1: { completed: false, data: {} },
            payment: { completed: false, data: {} },
            stage2: { completed: false, data: {} },
            submitted: false
          });
        }
      } else {
        console.log('❌ Dashboard: API call failed with status:', response.status);
        // For demo purposes, create mock data
        setApplicationStatus({
          stage1: { completed: false, data: {} },
          payment: { completed: false, data: {} },
          stage2: { completed: false, data: {} },
          submitted: false
        });
      }
    } catch (error) {
      console.error('❌ Dashboard: Error loading application status:', error);
      console.error('🔍 Dashboard: Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // Use consistent fallback data for all error cases
      console.log('⚠️ Dashboard: Using fallback data due to error');
      setApplicationStatus({
        stage1: { completed: false, data: {} },
        payment: { completed: false, data: {} },
        stage2: { completed: false, data: {} },
        submitted: false
      });
    } finally {
      setLoading(false);
      setIsUpdatingStatus(false);
    }
  };

  

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const refreshZohoData = async () => {
    try {
      console.log('🔄 Dashboard: Refreshing Zoho CRM data...');
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('❌ Dashboard: No token available for refresh');
        return;
      }
      
      const response = await fetch('/api/auth/refresh-zoho-data', {
        method: 'POST',
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Dashboard: Zoho CRM data refreshed successfully:', data);
        
        // Update the token in localStorage
        localStorage.setItem('token', data.token);
        
        // Reload the page to get fresh data
        window.location.reload();
      } else {
        console.log('❌ Dashboard: Failed to refresh Zoho CRM data:', response.status);
      }
    } catch (error) {
      console.error('❌ Dashboard: Error refreshing Zoho CRM data:', error);
    }
  };

  // Real-time payment status refresh (MongoDB priority)
  const refreshPaymentStatus = async () => {
    console.log('🔄 Dashboard: Refreshing payment status from MongoDB...');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('❌ Dashboard: No token available for payment refresh');
        return;
      }

      const response = await fetch('/api/dashboard/data', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('✅ Dashboard: Payment status refreshed from MongoDB:', responseData);
        
        // Extract data from the API response structure
        const data = responseData.data || responseData;
        
        // Update application status with fresh MongoDB data
        if (data && (data.dashboardStages || data.stage1 || data.payment || data.stage2)) {
          setApplicationStatus(data);
          console.log('🎯 Dashboard: Payment status updated in UI');
        }
      } else {
        console.log('❌ Dashboard: Failed to refresh payment status:', response.status);
      }
    } catch (error) {
      console.error('❌ Dashboard: Error refreshing payment status:', error);
    }
  };

  // Handle Stage 1 button click with loading state
  const handleStage1Click = () => {
    console.log('🔄 Dashboard: Stage 1 button clicked, showing loading...');
    setIsStage1Loading(true);
    
    // Navigate to Stage 1 page
    navigate('/stage1');
    
    // Reset loading state after a delay (in case navigation is slow)
    setTimeout(() => {
      setIsStage1Loading(false);
    }, 2000);
  };

  // Memoized stage statuses to prevent unnecessary recalculations and infinite loops
  const stage1Status = useMemo(() => {
    console.log('🔍 Dashboard: Calculating stage1 status');
    console.log('🔍 Dashboard: Current applicationStatus:', applicationStatus);
    console.log('🔍 Dashboard: Current user.zohoData:', user?.zohoData);
    
    // PRIORITY 1: MongoDB dashboardStages (real-time, most reliable)
    const mongoStage1Status = applicationStatus?.dashboardStages?.stage1?.status;
    if (mongoStage1Status === 'completed' || mongoStage1Status === 'submitted') {
      console.log('✅ Dashboard: Stage 1 completed based on MongoDB dashboardStages');
      return 'completed';
    }
    
    // PRIORITY 2: Application_Stage_1 document status
    const stage1DocStatus = applicationStatus?.applicationDetails?.stage1?.status;
    if (stage1DocStatus === 'completed' || stage1DocStatus === 'submitted') {
      console.log('✅ Dashboard: Stage 1 completed based on Application_Stage_1 document');
      return 'completed';
    }
    
    // PRIORITY 3: Check if user has Zoho CRM data indicating completion
    if (user?.zohoData?.applicationStage1Filled === 'Yes' || user?.zohoData?.formData?.applicationStage1Filled === 'Yes') {
      console.log('✅ Dashboard: Stage 1 completed based on Zoho CRM data');
      return 'completed';
    }
    
    // PRIORITY 4: Check localStorage for fresh Zoho data
    try {
      const zohoData = localStorage.getItem('zohoCRMData');
      if (zohoData) {
        const parsedZohoData = JSON.parse(zohoData);
        if (parsedZohoData?.formData?.applicationStage1Filled === 'Yes' || 
            parsedZohoData?.formData?.Application_Stage_1_Filled === 'Yes' ||
            parsedZohoData?.applicationStage1Filled === 'Yes' || 
            parsedZohoData?.Application_Stage_1_Filled === 'Yes') {
          console.log('✅ Dashboard: Stage 1 completed based on localStorage Zoho data');
          return 'completed';
        }
      }
    } catch (error) {
      console.log('⚠️ Dashboard: Error parsing localStorage Zoho data:', error);
    }
    
    // PRIORITY 5: Check legacy application status flag
    if (applicationStatus?.stage1?.completed === true) {
      console.log('✅ Dashboard: Stage 1 completed based on legacy application status flag');
      return 'completed';
    }
    
    console.log('⏳ Dashboard: Stage 1 pending');
    return 'pending';
  }, [user?.zohoData, applicationStatus?.dashboardStages?.stage1?.status, applicationStatus?.applicationDetails?.stage1?.status, applicationStatus?.stage1?.completed]);

  const stage2Status = useMemo(() => {
    console.log('🔍 Dashboard: Calculating stage2 status');
    
    if (!applicationStatus) return 'pending';
    
    // PRIORITY 1: MongoDB dashboardStages (real-time, most reliable)
    const mongoStage2Status = applicationStatus?.dashboardStages?.stage2?.status;
    if (mongoStage2Status === 'completed' || mongoStage2Status === 'submitted') {
      console.log('✅ Dashboard: Stage 2 completed based on MongoDB dashboardStages');
      return 'completed';
    }
    
    // PRIORITY 2: Application_Stage_2 document status
    const stage2DocStatus = applicationStatus?.applicationDetails?.stage2?.status;
    if (stage2DocStatus === 'completed' || stage2DocStatus === 'submitted') {
      console.log('✅ Dashboard: Stage 2 completed based on Application_Stage_2 document');
      return 'completed';
    }
    
    // PRIORITY 3: Legacy application status flag
    if (applicationStatus.stage2?.completed) {
      console.log('✅ Dashboard: Stage 2 completed based on legacy application status flag');
      return 'completed';
    }
    
    console.log('⏳ Dashboard: Stage 2 pending');
    return 'pending';
  }, [applicationStatus?.dashboardStages?.stage2?.status, applicationStatus?.applicationDetails?.stage2?.status, applicationStatus?.stage2?.completed]);

  const getStageStatus = (stage) => {
    if (stage === 'stage1') {
      return stage1Status;
    }
    if (stage === 'stage2') {
      return stage2Status;
    }
    return 'pending';
  };

  const getPaymentStatus = () => {
    if (!applicationStatus) return 'pending';
    
    // PRIORITY 1: MongoDB - Real-time, fast, reliable
    // Check MongoDB payment status from dashboardStages (most recent)
    const mongoPaymentStatus = applicationStatus.dashboardStages?.payment?.status;
    if (mongoPaymentStatus === 'completed') {
      console.log('🎯 Payment status: COMPLETED (MongoDB dashboardStages)');
      return 'completed';
    }
    
    // PRIORITY 2: Application_Stage_1 - Real-time payment details
    const stage1PaymentStatus = applicationStatus.applicationDetails?.stage1?.paymentStatus;
    if (stage1PaymentStatus === 'completed') {
      console.log('🎯 Payment status: COMPLETED (Application_Stage_1)');
      return 'completed';
    }
    
    // PRIORITY 3: Zoho CRM - Fallback for legacy data
    const paymentMode = applicationStatus.stage1?.Application_Fee_Payment_Mode;
    const paymentStatus = applicationStatus.stage1?.Payment_Status;
    if (paymentMode && paymentStatus === 'Completed') {
      console.log('🎯 Payment status: COMPLETED (Zoho CRM fallback)');
      return 'completed';
    }
    
    console.log('🎯 Payment status: PENDING (no completed status found)');
    return 'pending';
  };

  const getPaymentMode = () => {
    if (!applicationStatus) return null;
    return applicationStatus.stage1?.Application_Fee_Payment_Mode;
  };

  const getPaymentStatusText = () => {
    if (!applicationStatus) return 'Pending';
    
    const paymentStatus = getPaymentStatus();
    
    if (paymentStatus === 'completed') {
      // PRIORITY: Get payment mode from MongoDB (most recent and reliable)
      const paymentMode = applicationStatus.dashboardStages?.payment?.paymentMode ||
                         applicationStatus.applicationDetails?.stage1?.paymentMode ||
                         applicationStatus.stage1?.Application_Fee_Payment_Mode;
      
      return paymentMode ? `Completed (${paymentMode})` : 'Completed';
    }
    
    return 'Pending';
  };

  const canAccessStage2 = () => {
    return getStageStatus('stage1') === 'completed' && getPaymentStatus() === 'completed';
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'pending':
        return <Clock className="h-6 w-6 text-yellow-500" />;
      default:
        return <AlertCircle className="h-6 w-6 text-red-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'pending':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-red-50 border-red-200 text-red-800';
    }
  };

  // Show session expired if there's no token
  if (showSessionExpired) {
    return (
      <SessionExpired 
        onRetry={handleSessionRetry}
        onLogin={handleSessionLogin}
      />
    );
  }

  // Show token recovery if there's a token issue
  if (showTokenRecovery) {
    return (
      <TokenRecovery 
        onRetry={handleTokenRetry}
        onLogin={handleTokenLogin}
      />
    );
  }

  // Show loading if AuthContext is loading or if we're still loading application data
  if (authLoading || loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title="Dashboard" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Dashboard" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome back, {user?.name || 'Parent'}!
              </h1>
              <p className="text-gray-600">
                Track your child's admission application progress
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Application ID</p>
                <p className="font-mono text-sm font-medium text-gray-900">
                  {user?.id ? `APP-${user.id.slice(-6).toUpperCase()}` : 'APP-XXXXXX'}
                </p>
              </div>
                             <button
                 onClick={handleLogout}
                 className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
               >
                 Logout
               </button>
               {/* Note: Automatic data clearing after OTP verification has been removed to prevent login redirects */}
               {/* <button
                 onClick={() => {
                   console.log('🔄 Force refreshing all data...');
                   localStorage.removeItem('zohoCRMData');
                   localStorage.removeItem('token');
                   console.log('✅ All data cleared, reloading page...');
                   window.location.reload();
                 }}
                 className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors ml-2"
               >
                 🗑️ Clear All Data & Reload
               </button> */}
            </div>
          </div>
          
        </div>

        {/* Application Progress Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-teal-100 rounded-lg p-2">
              <FileText className="h-6 w-6 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Application Progress</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Stage 1 */}
          <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-teal-100 rounded-lg p-3">
                <FileText className="h-6 w-6 text-teal-600" />
              </div>
              {getStatusIcon(getStageStatus('stage1'))}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Application Stage 1</h3>
            <p className="text-sm text-gray-600 mb-4 min-h-[3rem]">
              Basic information and student details
            </p>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(getStageStatus('stage1'))} mb-4`}>
              {isUpdatingStatus ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></div>
                  Updating...
                </div>
              ) : (
                getStageStatus('stage1') === 'completed' ? 'Completed' : 'Pending'
              )}
            </div>
            
            {/* Action Button - Positioned at bottom */}
            <div className="mt-auto">
              <button
                onClick={handleStage1Click}
                disabled={isStage1Loading}
                className={`w-full p-3 rounded-lg transition-colors text-center block ${
                  isStage1Loading 
                    ? 'bg-teal-400 cursor-not-allowed' 
                    : 'bg-teal-600 hover:bg-teal-700 cursor-pointer'
                }`}
              >
                <div className="flex items-center justify-center">
                  {isStage1Loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      <span className="text-sm font-medium">Loading...</span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-medium">
                        {getStageStatus('stage1') === 'completed' ? 'View/Edit' : 'Start Application'}
                      </span>
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-teal-100 rounded-lg p-3">
                <CreditCard className="h-6 w-6 text-teal-600" />
              </div>
              {getStatusIcon(getPaymentStatus())}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Application Fees</h3>
            <p className="text-sm text-gray-600 mb-4 min-h-[3rem]">
              Application fee payment ({applicationFeeDisplay})
            </p>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(getPaymentStatus())} mb-4`}>
              {isUpdatingStatus ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></div>
                  Updating...
                </div>
              ) : (
                <div className="flex items-center">
                  {getPaymentStatus() === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                  {getPaymentStatusText()}
                </div>
              )}
            </div>
            
            {/* Action Button - Positioned at bottom */}
            <div className="mt-auto">
              {getStageStatus('stage1') === 'completed' ? (
                getPaymentStatus() === 'completed' ? (
                  // Payment completed - show disabled button
                  <div className="w-full p-3 rounded-lg bg-green-100 text-green-800 text-center cursor-not-allowed border border-green-200">
                    <div className="flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">Payment Completed</span>
                    </div>
                  </div>
                ) : (
                  // Payment pending - show active Pay Now button
                  <Link
                    to="/payment"
                    className="w-full p-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white cursor-pointer transition-colors text-center block"
                  >
                    <div className="flex items-center justify-center">
                      <span className="text-sm font-medium">Pay Now</span>
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </div>
                  </Link>
                )
              ) : (
                // Stage 1 not completed - show disabled button
                <div className="w-full p-3 rounded-lg bg-gray-300 text-gray-500 cursor-not-allowed text-center">
                  <span className="text-sm font-medium">Complete Stage 1 First</span>
                </div>
              )}
            </div>
          </div>

          {/* Stage 2 */}
          <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-teal-100 rounded-lg p-3">
                <ClipboardList className="h-6 w-6 text-teal-600" />
              </div>
              {getStatusIcon(getStageStatus('stage2'))}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Application Stage II</h3>
            <p className="text-sm text-gray-600 mb-4 min-h-[3rem]">
              Detailed information and documents
            </p>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(getStageStatus('stage2'))} mb-4`}>
              {isUpdatingStatus ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></div>
                  Updating...
                </div>
              ) : (
                getStageStatus('stage2') === 'completed' ? 'Completed' : 'Pending'
              )}
            </div>
            
            {/* Action Button - Coming Soon */}
            <div className="mt-auto">
              <button
                disabled
                className="w-full p-3 rounded-lg text-center block bg-gray-200 text-gray-400 cursor-not-allowed"
              >
                <div className="flex items-center justify-center">
                  <span className="text-sm font-medium">Coming Soon</span>
                </div>
              </button>
            </div>
          </div>

          {/* Review */}
          <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-teal-100 rounded-lg p-3">
                <CheckCircle className="h-6 w-6 text-teal-600" />
              </div>
              {getStatusIcon(applicationStatus?.submitted ? 'completed' : 'pending')}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Approval by Counsellor</h3>
            <p className="text-sm text-gray-600 mb-4 min-h-[3rem]">
              Final review and approval
            </p>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(applicationStatus?.submitted ? 'completed' : 'pending')} mb-4`}>
              {applicationStatus?.submitted ? 'Approved' : 'Pending'}
            </div>
            
            {/* Action Button - Positioned at bottom */}
            <div className="mt-auto">
              <Link
                to={getStageStatus('stage2') === 'completed' ? '/application-review' : '#'}
                className={`w-full p-3 rounded-lg transition-colors text-center block ${
                  getStageStatus('stage2') === 'completed' 
                    ? 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center justify-center">
                  <span className="text-sm font-medium">
                    {getStageStatus('stage2') === 'completed' ? 'Submit for Review' : 'Locked'}
                  </span>
                  {getStageStatus('stage2') === 'completed' && <ArrowRight className="h-4 w-4 ml-2" />}
                </div>
              </Link>
            </div>
          </div>

          </div>
        </div>

        {/* School Life Section - Only visible after enrollment */}
        {applicationStatus?.submitted && (
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-blue-100 rounded-lg p-2">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">School Life</h2>
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                Available After Enrollment
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Outing Management */}
              <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full border-2 border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-blue-100 rounded-lg p-3">
                    <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="bg-green-100 rounded-lg p-1">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Outing Management</h3>
                <p className="text-sm text-gray-600 mb-4 min-h-[3rem]">
                  Manage your child's outing requests and permissions
                </p>
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-4">
                  Available
                </div>
                
                {/* Action Button - Positioned at bottom */}
                <div className="mt-auto">
                  <Link
                    to="/outing/parent"
                    className="w-full p-3 rounded-lg transition-colors text-center block bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                  >
                    <div className="flex items-center justify-center">
                      <span className="text-sm font-medium">Manage Outings</span>
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </div>
                  </Link>
                </div>
              </div>

              {/* Attendance Tracking - Placeholder for future features */}
              <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full border-2 border-gray-200 opacity-60">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-500 mb-2">Attendance Tracking</h3>
                <p className="text-sm text-gray-400 mb-4 min-h-[3rem]">
                  Monitor your child's daily attendance and punctuality
                </p>
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 mb-4">
                  Coming Soon
                </div>
                
                {/* Action Button - Positioned at bottom */}
                <div className="mt-auto">
                  <button
                    disabled
                    className="w-full p-3 rounded-lg text-center block bg-gray-200 text-gray-400 cursor-not-allowed"
                  >
                    <div className="flex items-center justify-center">
                      <span className="text-sm font-medium">Coming Soon</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Academic Progress - Placeholder for future features */}
              <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full border-2 border-gray-200 opacity-60">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-500 mb-2">Academic Progress</h3>
                <p className="text-sm text-gray-400 mb-4 min-h-[3rem]">
                  View grades, assignments, and academic performance
                </p>
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 mb-4">
                  Coming Soon
                </div>
                
                {/* Action Button - Positioned at bottom */}
                <div className="mt-auto">
                  <button
                    disabled
                    className="w-full p-3 rounded-lg text-center block bg-gray-200 text-gray-400 cursor-not-allowed"
                  >
                    <div className="flex items-center justify-center">
                      <span className="text-sm font-medium">Coming Soon</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pre-Enrollment Notice */}
        {!applicationStatus?.submitted && (
          <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-blue-800">Complete Your Application First</h3>
            </div>
            <p className="text-blue-700 mb-3">
              School life features like outing management, attendance tracking, and academic progress will become available once your application is approved and your child is enrolled.
            </p>
            <p className="text-sm text-blue-600">
              Focus on completing the application stages above to unlock these features.
            </p>
          </div>
        )}


        {/* Horizontal Progress Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Application Progress</h2>
            <span className="text-lg font-semibold text-teal-600">
              {(() => {
                let completed = 0;
                let total = 4;
                if (getStageStatus('stage1') === 'completed') completed++;
                if (getPaymentStatus() === 'completed') completed++;
                if (getStageStatus('stage2') === 'completed') completed++;
                if (applicationStatus?.submitted) completed++;
                return `${Math.round((completed / total) * 100)}%`;
              })()}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-teal-600 h-3 rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${(() => {
                  let completed = 0;
                  let total = 4;
                  if (getStageStatus('stage1') === 'completed') completed++;
                  if (getPaymentStatus() === 'completed') completed++;
                  if (getStageStatus('stage2') === 'completed') completed++;
                  if (applicationStatus?.submitted) completed++;
                  return (completed / total) * 100;
                })()}%`
              }}
            ></div>
          </div>
          <div className="flex justify-between text-sm text-gray-600 mt-2">
            <span>Stage 1</span>
            <span>Payment</span>
            <span>Stage 2</span>
            <span>Approval</span>
          </div>
        </div>

        {/* Notices Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Important Notices & Updates</h2>
          <div className="space-y-4">
            <div className="border-l-4 border-teal-500 pl-4 py-2">
              <h3 className="font-medium text-gray-900">Application Deadline</h3>
              <p className="text-sm text-gray-600">Complete your application by March 31, 2025 for the 2025-26 academic year.</p>
            </div>
            <div className="border-l-4 border-yellow-500 pl-4 py-2">
              <h3 className="font-medium text-gray-900">Document Verification</h3>
              <p className="text-sm text-gray-600">Please ensure all documents are properly scanned and uploaded in Stage 2.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4 py-2">
              <h3 className="font-medium text-gray-900">Payment Confirmation</h3>
              <p className="text-sm text-gray-600">Payment confirmation will be sent to your registered email within 24 hours.</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-2">
              <h3 className="font-medium text-gray-900">Interview Schedule</h3>
              <p className="text-sm text-gray-600">Interviews will be scheduled after Stage 2 completion. You'll receive an email notification.</p>
            </div>
          </div>
        </div>

        {/* User Information */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Admission Counsellor</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-3">
              <User className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{user?.name || 'Not provided'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-900">{user?.phone || 'Not provided'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{user?.email || 'Not provided'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
